import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  Radio,
  TextField,
  Typography,
} from '@mui/material';
import { getEligibleInvoicesForSOA } from '../../api';

const money = (n) =>
  `$${(Number(n) || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const day = (v) => (v ? String(v).slice(0, 10) : '');

/**
 * Pick the approved invoice that paid for a deduction. The server only offers
 * invoices for this SOA's property, building and unit that carry a unit number
 * and are not already used on another statement.
 */
const SelectInvoiceModal = ({ isOpen, onClose, propertyDetails, soaId, currentInvoiceId, onSelect }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState(null);

  const propertyId = propertyDetails?.property_id ? String(propertyDetails.property_id) : '';
  const unitNo = String(propertyDetails?.unit_no || '').trim();
  const buildingName = String(propertyDetails?.building_name || '').trim();
  const ready = !!propertyId && !!unitNo;

  useEffect(() => {
    if (!isOpen) return undefined;
    setPicked(currentInvoiceId ? String(currentInvoiceId) : null);
    setSearch('');
    if (!ready) {
      setRows([]);
      return undefined;
    }
    let active = true;
    setLoading(true);
    setError('');
    getEligibleInvoicesForSOA({ propertyId, buildingName, unitNo, soaId: soaId || undefined })
      .then((list) => active && setRows(Array.isArray(list) ? list : []))
      .catch((e) => active && setError(e?.message || 'Failed to load eligible invoices'))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [isOpen, ready, propertyId, buildingName, unitNo, soaId, currentInvoiceId]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [`rd ${r.invoiceNo ?? ''}`, r.title, r.vendorName, r.jobDescription].join(' ').toLowerCase().includes(q)
    );
  }, [rows, search]);

  const chosen = rows.find((r) => String(r._id) === picked) || null;

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="select-invoice-title">
      <DialogTitle id="select-invoice-title">Attach an invoice to this deduction (optional)</DialogTitle>
      <DialogContent dividers>
        {!ready ? (
          <Alert severity="info">Choose the property, building and unit first — the invoices offered belong to that unit.</Alert>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Approved invoices for {buildingName ? `${buildingName}, ` : ''}unit {unitNo} that are not used on another statement.
            </Typography>
            <TextField
              size="small"
              fullWidth
              placeholder="Search by RD number, vendor or title"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              inputProps={{ 'aria-label': 'Search eligible invoices' }}
              sx={{ mb: 1 }}
            />
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : error ? (
              <Alert severity="error">{error}</Alert>
            ) : visible.length === 0 ? (
              <Alert severity="warning">
                {rows.length === 0
                  ? 'No approved invoice for this unit is available. Approve the invoice in the Invoices module first, and make sure its line carries this unit number. If the work was done in-house, leave the deduction without an invoice.'
                  : 'No invoice matches the search.'}
              </Alert>
            ) : (
              <List dense disablePadding>
                {visible.map((r) => {
                  const id = String(r._id);
                  return (
                    <ListItemButton
                      key={id}
                      selected={picked === id}
                      onClick={() => setPicked(id)}
                      role="radio"
                      aria-checked={picked === id}
                      sx={{ borderRadius: 1 }}
                    >
                      <Radio edge="start" checked={picked === id} tabIndex={-1} size="small" />
                      <ListItemText
                        primary={`RD ${r.invoiceNo ?? '—'} · ${r.title || 'Service'}`}
                        secondary={[
                          r.vendorName,
                          r.unitNo ? `Unit ${r.unitNo}` : '',
                          r.jobDescription,
                          day(r.invoiceDate),
                          money(r.total),
                          r.status,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!chosen}
          onClick={() => {
            if (chosen) onSelect(chosen);
          }}
        >
          Attach invoice
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SelectInvoiceModal;
